import { Check, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

export type CompareColumn = 'MANUAL' | 'FREE' | 'STANDARD' | 'VIP'

const COLUMNS: CompareColumn[] = ['MANUAL', 'FREE', 'STANDARD', 'VIP']

type CellKind = 'no' | 'yes' | 'na' | 'text'

interface CompareRow {
  key: string
  hint?: string
  cells: Record<CompareColumn, { kind: CellKind; text?: string; highlight?: boolean }>
}

interface PlanEconomicsDisplay {
  standardPrice: string
  standardPoints: number
  vipPrice: string
  vipPoints: number
  standardOfferCost: string
  vipOfferCost: string
  topupDiscount: string
  freeAccounts: string
  standardAccounts: string
  vipAccounts: string
  freeBundles: string
  standardBundles: string
  vipBundles: string
}

function CompareCell({
  kind,
  text,
  highlight,
}: {
  kind: CellKind
  text?: string
  highlight?: boolean
}) {
  if (kind === 'yes') {
    return (
      <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
        <Check className="h-4 w-4" strokeWidth={2.5} />
      </span>
    )
  }
  if (kind === 'no') {
    return (
      <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-red-50 text-red-400">
        <X className="h-4 w-4" strokeWidth={2.5} />
      </span>
    )
  }
  if (kind === 'na') {
    return <span className="text-gray-300 text-sm">—</span>
  }
  return (
    <span
      className={`text-xs sm:text-sm leading-snug ${
        highlight ? 'font-bold text-emerald-800' : 'font-medium text-gray-900'
      }`}
    >
      {text}
    </span>
  )
}

function columnHeaderClass(col: CompareColumn): string {
  switch (col) {
    case 'MANUAL':
      return 'bg-gray-100 text-gray-500'
    case 'FREE':
      return 'bg-orange-50 text-[#FF5A00] ring-1 ring-inset ring-orange-200'
    case 'STANDARD':
      return 'bg-slate-50 text-slate-800 ring-1 ring-inset ring-slate-200'
    case 'VIP':
      return 'bg-gradient-to-b from-violet-50 to-emerald-50/80 text-violet-900 ring-2 ring-inset ring-violet-300'
    default:
      return ''
  }
}

function columnBodyClass(col: CompareColumn, highlight?: boolean): string {
  const base = 'px-2 py-2.5 text-center align-middle'
  switch (col) {
    case 'MANUAL':
      return `${base} bg-gray-50/60`
    case 'FREE':
      return `${base} bg-orange-50/30`
    case 'STANDARD':
      return `${base} bg-slate-50/40`
    case 'VIP':
      return `${base} ${highlight ? 'bg-emerald-50/90' : 'bg-violet-50/25'}`
    default:
      return base
  }
}

export function UpgradePlanCompareTable({
  economics,
  currentTier = 'FREE',
}: {
  economics: PlanEconomicsDisplay
  currentTier?: 'FREE' | 'STANDARD'
}) {
  const { t } = useTranslation('credits')

  const yes = { kind: 'yes' as const }
  const no = { kind: 'no' as const }
  const na = { kind: 'na' as const }

  const rows: CompareRow[] = [
    {
      key: 'allegroIntegration',
      cells: { MANUAL: no, FREE: yes, STANDARD: yes, VIP: yes },
    },
    {
      key: 'descriptionStructure',
      cells: { MANUAL: no, FREE: yes, STANDARD: yes, VIP: yes },
    },
    {
      key: 'repeatability',
      cells: { MANUAL: no, FREE: yes, STANDARD: yes, VIP: yes },
    },
    {
      key: 'bulkProcessing',
      cells: { MANUAL: no, FREE: yes, STANDARD: yes, VIP: yes },
    },
    {
      key: 'templatesPrompts',
      cells: { MANUAL: no, FREE: yes, STANDARD: yes, VIP: yes },
    },
    {
      key: 'allegroAccounts',
      cells: {
        MANUAL: na,
        FREE: { kind: 'text', text: economics.freeAccounts },
        STANDARD: { kind: 'text', text: economics.standardAccounts },
        VIP: { kind: 'text', text: economics.vipAccounts, highlight: true },
      },
    },
    {
      key: 'flexibleBundles',
      cells: {
        MANUAL: na,
        FREE: { kind: 'text', text: economics.freeBundles },
        STANDARD: { kind: 'text', text: economics.standardBundles },
        VIP: { kind: 'text', text: economics.vipBundles, highlight: true },
      },
    },
    {
      key: 'monthlyPackage',
      cells: {
        MANUAL: na,
        FREE: { kind: 'text', text: t('upgradeTopBar.values.freePackage') },
        STANDARD: {
          kind: 'text',
          text: t('upgradeTopBar.values.paidPackage', {
            price: economics.standardPrice,
            points: economics.standardPoints,
          }),
        },
        VIP: {
          kind: 'text',
          text: t('upgradeTopBar.values.paidPackage', {
            price: economics.vipPrice,
            points: economics.vipPoints,
          }),
          highlight: true,
        },
      },
    },
    {
      key: 'fullOfferCost',
      cells: {
        MANUAL: na,
        FREE: { kind: 'text', text: t('upgradeTopBar.values.fullPrice') },
        STANDARD: {
          kind: 'text',
          text: t('upgradeTopBar.values.fullOfferCost', { cost: economics.standardOfferCost }),
        },
        VIP: {
          kind: 'text',
          text: t('upgradeTopBar.values.fullOfferCostVip', { cost: economics.vipOfferCost }),
          highlight: true,
        },
      },
    },
    {
      key: 'topupDiscount',
      cells: {
        MANUAL: na,
        FREE: { kind: 'text', text: t('upgradeTopBar.values.fullPrice') },
        STANDARD: { kind: 'text', text: economics.topupDiscount },
        VIP: { kind: 'text', text: economics.topupDiscount, highlight: true },
      },
    },
  ]

  return (
    <div className="overflow-x-auto -mx-1">
      <table className="w-full min-w-[640px] text-sm border-collapse">
        <thead>
          <tr>
            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 border-b border-gray-200 w-[26%]">
              {t('upgradeTopBar.colFeature')}
            </th>
            {COLUMNS.map((col) => (
              <th
                key={col}
                className={`px-2 py-2.5 text-center text-xs font-bold uppercase tracking-wide border-b border-gray-200 ${columnHeaderClass(col)}`}
              >
                <div className="flex flex-col items-center gap-0.5">
                  {t(`upgradeTopBar.plans.${col}`)}
                  {col === 'MANUAL' && (
                    <span className="text-[10px] font-medium normal-case tracking-normal text-gray-400">
                      {t('upgradeTopBar.plans.manualHint')}
                    </span>
                  )}
                  {col === currentTier && (
                    <span className="text-[10px] font-bold normal-case tracking-normal">
                      {t('upgradeTopBar.currentPlan')}
                    </span>
                  )}
                  {col === 'VIP' && (
                    <span className="rounded-full bg-violet-600 px-2 py-0.5 text-[9px] font-bold normal-case tracking-normal text-white">
                      {t('upgradeTopBar.plans.recommendedBadge')}
                    </span>
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row) => (
            <tr key={row.key}>
              <td className="px-3 py-2.5 font-medium text-gray-700 align-middle">
                <span className="text-sm">{t(`upgradeTopBar.rows.${row.key}`)}</span>
                {row.hint && (
                  <p className="mt-0.5 text-[10px] font-normal text-gray-400 leading-snug">{row.hint}</p>
                )}
              </td>
              {COLUMNS.map((col) => {
                const cell = row.cells[col]
                return (
                  <td key={col} className={columnBodyClass(col, cell.highlight)}>
                    <div className="flex justify-center">
                      <CompareCell kind={cell.kind} text={cell.text} highlight={cell.highlight} />
                    </div>
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
